import { Lives } from './Lives';
import { TokenWallet } from './TokenWallet';

const KEY_SCORES = 'lavascend.scores';
const KEY_BEST_LEGACY = 'lavascend.best';
const KEY_PLAYED = 'lavascend.played';

export const MAX_SCORES = 10;

export interface Score {
  level: number;
  date: string; // yyyy-mm-dd
}

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isValidScore(x: unknown): x is Score {
  return (
    typeof x === 'object' && x !== null &&
    typeof (x as Score).level === 'number' && Number.isFinite((x as Score).level) &&
    typeof (x as Score).date === 'string'
  );
}

function loadScores(): Score[] {
  const raw = localStorage.getItem(KEY_SCORES);
  if (raw != null) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const valid = parsed.filter(isValidScore);
        valid.sort((a, b) => b.level - a.level);
        return valid.slice(0, MAX_SCORES);
      }
    } catch { /* fall through */ }
    // Corrupt or wrong-shape — drop it so the next session starts clean.
    localStorage.removeItem(KEY_SCORES);
    return [];
  }
  // Migrate from old single-best key, if any.
  const oldBest = localStorage.getItem(KEY_BEST_LEGACY);
  localStorage.removeItem(KEY_BEST_LEGACY);
  if (oldBest != null) {
    const n = parseInt(oldBest, 10);
    if (Number.isFinite(n) && n >= 1) {
      const migrated: Score[] = [{ level: n, date: todayISO() }];
      localStorage.setItem(KEY_SCORES, JSON.stringify(migrated));
      return migrated;
    }
  }
  return [];
}

export class GameState {
  level: number = 1;
  readonly scores: Score[];
  readonly lives = new Lives();
  readonly wallet: TokenWallet;

  constructor() {
    this.scores = loadScores();
    this.wallet = new TokenWallet();
  }

  get best(): number {
    return this.scores[0]?.level ?? 0;
  }

  recordRun(level: number): void {
    if (!Number.isFinite(level) || level < 1) return;
    this.scores.push({ level: Math.floor(level), date: todayISO() });
    this.scores.sort((a, b) => b.level - a.level);
    if (this.scores.length > MAX_SCORES) this.scores.length = MAX_SCORES;
    localStorage.setItem(KEY_SCORES, JSON.stringify(this.scores));
  }

  // Per-run reset: level + lives only. The token wallet is persistent meta-currency
  // and is not cleared between runs.
  reset(): void {
    this.level = 1;
    this.lives.reset();
  }

  hasPlayed(): boolean { return localStorage.getItem(KEY_PLAYED) === '1'; }
  markPlayed(): void { localStorage.setItem(KEY_PLAYED, '1'); }
}

export const gameState = new GameState();
